import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AssignmentModal from '../../../shared/ui/assignment-modal/assignment-modal.jsx';
import EntityLink from '../../../shared/ui/entity-link/entity-link.jsx';
import LoadingContainer from '../../../shared/ui/loading-container/loading-container.jsx';

const TICKET_STATUSES = ['new', 'pending_support', 'pending_client', 'closed'];

const STATUS_BADGE_CLASS = {
  new: 'bg-secondary-subtle text-secondary',
  pending_support: 'bg-warning-subtle text-warning',
  pending_client: 'bg-success-subtle text-success',
  closed: 'app-badge-disabled',
};

function StatusBadge({ status, t }) {
  const STATUS_LABELS = {
    new: t('support.statusNew'),
    pending_support: t('support.statusInProgress'),
    pending_client: t('support.statusAwaitingClient'),
    closed: t('support.statusClosed'),
  };
  const label = STATUS_LABELS[status] || status || '-';
  const cls = STATUS_BADGE_CLASS[status] || 'app-badge-disabled';
  return <span className={`badge ${cls}`}>{label}</span>;
}

function SupportTicketsPageView({
  rows,
  total,
  isLoading,
  error,
  filters,
  isFilterOpen,
  setIsFilterOpen,
  toggleStatus,
  resetFilters,
  onAssignedOnlyChange,
  // assign modal (for row inline assign)
  isAssignModalOpen,
  adminProfileOptions,
  isAdminProfilesLoading,
  selectedManagerId,
  isAssignmentSaving,
  assignmentError,
  openAssignModal,
  closeAssignModal,
  onSelectManager,
  assignManager,
  unassignManager,
  assignTargetName,
}) {
  const { t } = useTranslation();

  const STATUS_LABELS = {
    new: t('support.statusNew'),
    pending_support: t('support.statusInProgress'),
    pending_client: t('support.statusAwaitingClient'),
    closed: t('support.statusClosed'),
  };

  const CATEGORY_LABELS = {
    general: t('support.categoryGeneral'),
    deposit_withdrawal: t('support.categoryDepositWithdrawal'),
    trading: t('support.categoryTrading'),
  };

  return (
    <div className="card">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h5 className="card-title mb-0">{t('support.title')}</h5>
            {total > 0 ? (
              <small className="text-muted">{total} {total !== 1 ? t('support.tickets') : t('support.ticket')}</small>
            ) : null}
          </div>
          <div className="d-flex gap-2">
            <div className="form-check form-switch mb-0 d-flex align-items-center gap-2">
              <input
                className="form-check-input"
                type="checkbox"
                id="assignedOnlyToggle"
                checked={filters.assignedOnly}
                onChange={(event) => onAssignedOnlyChange(event.target.checked)}
              />
              <label className="form-check-label" htmlFor="assignedOnlyToggle">{t('profiles.mineOnly')}</label>
            </div>
            <button
              type="button"
              className={`btn btn-sm ${isFilterOpen ? 'btn-primary' : 'btn-outline-secondary'}`}
              onClick={() => setIsFilterOpen((prev) => !prev)}
            >
              <i className="ti ti-filter me-1" aria-hidden="true"></i>
              {t('profiles.filters')}
            </button>
          </div>
        </div>

        {isFilterOpen ? (
          <div className="border rounded p-3 mb-3 bg-body-tertiary">
            <div className="row g-2 align-items-end">
              <div className="col-12">
                <label className="form-label mb-1 small">{t('support.filterStatus')}</label>
                <div className="d-flex flex-wrap gap-2">
                  {TICKET_STATUSES.map((status) => (
                    <button
                      key={status}
                      type="button"
                      className={`btn btn-sm ${filters.statuses.includes(status) ? 'btn-primary' : 'btn-outline-secondary'}`}
                      onClick={() => toggleStatus(status)}
                    >
                      {STATUS_LABELS[status] || status}
                    </button>
                  ))}
                </div>
              </div>
              <div className="col-12 d-flex justify-content-end">
                <button type="button" className="btn btn-sm btn-link text-muted" onClick={resetFilters}>
                  {t('support.resetClose')}
                </button>
              </div>
            </div>
          </div>
        ) : null}

        {error ? <div className="alert alert-danger">{error}</div> : null}

        <LoadingContainer isLoading={isLoading} hasData={rows.length > 0}>
          {rows.length === 0 && !isLoading ? (
            <p className="text-muted mb-0">{t('support.noTicketsFound')}</p>
          ) : (
            <table className="table align-middle mb-0">
              <thead>
                <tr>
                  <th style={{ width: '70px' }}>{t('support.colNumber')}</th>
                  <th>{t('support.colSubject')}</th>
                  <th style={{ width: '160px' }}>{t('support.colCategory')}</th>
                  <th style={{ width: '130px' }}>{t('support.colStatus')}</th>
                  <th style={{ width: '150px' }}>{t('support.colClient')}</th>
                  <th style={{ width: '150px' }}>{t('support.colAssignedTo')}</th>
                  <th style={{ width: '160px' }}>{t('support.colCreated')}</th>
                  <th style={{ width: '80px' }}></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.key}>
                    <td className="text-muted small">{row.number ?? '—'}</td>
                    <td>
                      <Link to={`/support/${row.uid}`} className="text-decoration-none fw-semibold">
                        {row.subject}
                      </Link>
                    </td>
                    <td className="text-muted small">{CATEGORY_LABELS[row.category] || row.category || '—'}</td>
                    <td><StatusBadge status={row.status} t={t} /></td>
                    <td className="small">
                      <EntityLink to={`/profiles/${row.profileId}`} className="text-decoration-none">
                        {row.profileName}
                      </EntityLink>
                    </td>
                    <td className="small">{row.assignedName || <span className="text-muted">—</span>}</td>
                    <td className="text-muted small">{row.createdAt}</td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-sm btn-default"
                        title={t('support.assign')}
                        onClick={() => openAssignModal(row)}
                      >
                        <i className="ti ti-user-plus" aria-hidden="true"></i>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </LoadingContainer>
      </div>

      <AssignmentModal
        isOpen={isAssignModalOpen}
        title={t('support.assignTicket')}
        targetLabel={t('support.colClient')}
        targetName={assignTargetName}
        error={assignmentError}
        options={adminProfileOptions}
        isOptionsLoading={isAdminProfilesLoading}
        selectedId={selectedManagerId}
        isSaving={isAssignmentSaving}
        showUnassign={Boolean(assignTargetName)}
        onClose={closeAssignModal}
        onSelect={onSelectManager}
        onAssign={assignManager}
        onUnassign={unassignManager}
      />
    </div>
  );
}

export default SupportTicketsPageView;
